/**
 * What the Planner reads from Collab, and the facts about an upload it proxies (KTD1, KTD3,
 * KTD5, KTD8). Collab owns materials; the Planner keeps only what these parsers let through,
 * so an extra field in a Collab answer never reaches a teacher or D1. Pure.
 */
import { MATERIAL_ID } from './days';
import type { ItemMaterial } from './types';

/** One material in "Attach from Collab" (R3): newest first, with its group's name. */
export interface CollabMaterialListing {
  id: string;
  groupId: string;
  groupName: string;
  title: string;
  filename: string;
  createdAt: string;
}

/** Collab's own refusal, passed through to the teacher (R7). Codes only, never prose. */
export interface CollabRefusalBody {
  error: string;
  reason?: string;
}

/** Collab's body cap for an upload: 10 MB of file plus 64 KiB of multipart framing (KTD5). */
export const MAX_UPLOAD_BODY_BYTES = 10 * 1024 * 1024 + 64 * 1024;

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const text = (value: unknown, max = 500): value is string => typeof value === 'string' && value.length <= max;
const CODE = /^[a-z][a-z0-9_]{0,63}$/;

/** `GET /api/me/materials`'s body, or null when any entry is not a material. */
export function parseListing(body: unknown): CollabMaterialListing[] | null {
  const materials = record(body)?.materials;
  if (!Array.isArray(materials)) return null;
  const out: CollabMaterialListing[] = [];
  for (const entry of materials) {
    const m = record(entry);
    if (!m || !text(m.id) || !MATERIAL_ID.test(m.id) || !text(m.groupId) || !MATERIAL_ID.test(m.groupId)
      || !text(m.groupName) || !text(m.title) || !text(m.filename) || !text(m.createdAt, 64)) return null;
    out.push({ id: m.id, groupId: m.groupId, groupName: m.groupName, title: m.title, filename: m.filename, createdAt: m.createdAt });
  }
  return out;
}

/**
 * `GET /api/materials/:id` or an upload's body as an attachment snapshot (R4, R9), or null.
 * With `expectedId`, an answer about any other material is null too.
 */
export function parseSnapshot(body: unknown, expectedId?: string): ItemMaterial | null {
  const m = record(body);
  if (!m || !text(m.id) || !MATERIAL_ID.test(m.id) || !text(m.groupId) || !MATERIAL_ID.test(m.groupId) || !text(m.title) || !text(m.filename)) return null;
  if (expectedId !== undefined && m.id !== expectedId) return null;
  return { materialId: m.id, groupId: m.groupId, title: m.title, filename: m.filename };
}

/** An upload Collab stored: the attachment snapshot, and the size Collab counted, for the event's bucket. */
export interface UploadedMaterial {
  material: ItemMaterial;
  /** The stored file's bytes, as Collab measured them — not the multipart body's length (KTD8). */
  sizeBytes: number;
}

/**
 * `POST /api/planner-uploads`'s 201 body, `{ groupId, material: { id, groupId, title, filename, sizeBytes, … } }`,
 * as an attachment snapshot and its size, or null. The rest of the material — uploader, note — stays in Collab.
 */
export function parseUploaded(body: unknown): UploadedMaterial | null {
  const b = record(body);
  const material = record(b?.material);
  if (!b || !material) return null;
  const sizeBytes = material.sizeBytes;
  if (typeof sizeBytes !== 'number' || !Number.isSafeInteger(sizeBytes) || sizeBytes < 0) return null;
  const snapshot = parseSnapshot({ ...material, groupId: b.groupId ?? material.groupId });
  return snapshot ? { material: snapshot, sizeBytes } : null;
}

/** Collab's `{ error, reason? }`, codes only; null when `error` is not a code. */
export function parseRefusal(body: unknown): CollabRefusalBody | null {
  const b = record(body);
  if (!b || !text(b.error, 64) || !CODE.test(b.error)) return null;
  return { error: b.error, ...(text(b.reason, 64) && CODE.test(b.reason) ? { reason: b.reason } : {}) };
}

export type UploadLength = { ok: true; length: number } | { ok: false; status: 411 | 413; error: 'length_required' | 'material_too_large' };

/** KTD5: a declared length is required, and over Collab's cap is refused before any byte moves. */
export function decideUploadLength(header: string | null): UploadLength {
  if (header === null || !/^\d{1,12}$/.test(header)) return { ok: false, status: 411, error: 'length_required' };
  const length = Number(header);
  if (length > MAX_UPLOAD_BODY_BYTES) return { ok: false, status: 413, error: 'material_too_large' };
  return { ok: true, length };
}

export type SizeBucket = 'under_100kb' | 'under_1mb' | 'under_5mb' | 'under_10mb';

/** Collab's buckets (`packages/collab/core/materials.ts`), so the two products' events compare. */
export function sizeBucket(bytes: number): SizeBucket {
  if (bytes < 100 * 1024) return 'under_100kb';
  if (bytes < 1024 * 1024) return 'under_1mb';
  if (bytes < 5 * 1024 * 1024) return 'under_5mb';
  return 'under_10mb';
}

export type MaterialFormat = 'pdf' | 'docx' | 'pptx' | 'png' | 'jpeg' | 'text' | 'other';

const FORMATS: Record<string, MaterialFormat> = { pdf: 'pdf', docx: 'docx', pptx: 'pptx', png: 'png', jpg: 'jpeg', jpeg: 'jpeg', txt: 'text' };

/** Collab's format names, read from the stored filename's extension: the Planner never reads the file. */
export function materialFormat(filename: string): MaterialFormat {
  const dot = filename.lastIndexOf('.');
  return dot < 0 ? 'other' : FORMATS[filename.slice(dot + 1).toLowerCase()] ?? 'other';
}
